// useRecipeActions.js — Recipe interaction handlers: dislike, replace, remove, remove-all
import { useState, useCallback } from "react";

import { smartRecalculateBulkRemoval } from "../../../utils/smartRecalculation";
import { trackRecipeViewed, trackRecipeReplaced, MEAL_TYPE } from "../../../analytics";

import { fetchRecipeDetailsAndGenerateImage } from "../recipeProcessingService";
import { handleRemoveRecipeAsync } from "../AlternativeRecipes";
import { buildStorageMap, createStorageEnricher } from "../utils/mealPlanHelpers";

/**
 * Recipe interaction handlers extracted from MealPlan.
 *
 * @param {Object} deps
 * @param {Array} deps.mealPlan
 * @param {Function} deps.setMealPlan
 * @param {Array} deps.productList
 * @param {Function} deps.setProductList
 * @param {Array} deps.leftovers
 * @param {Function} deps.setLeftovers
 * @param {Function} deps.setTotalCost
 * @param {Function} deps.setAdjustedTotalCost
 * @param {string} deps.budget
 * @param {number} deps.numberOfPeople
 * @param {string|null} deps.userCountry
 * @param {Object} deps.cookingStyle
 * @param {Array} deps.dietaryPreferences
 * @param {string} deps.selectedCuisine
 * @param {Function} deps.generateGroceryList
 * @param {Function} deps.addReasoningStep
 * @param {boolean} deps.groceryOperationInProgress
 * @param {Function} deps.setGroceryOperationInProgress
 * @param {Function} deps.setImagesLoading
 * @param {boolean} deps.isPremium
 * @param {Function} deps.onNavigateToWizard
 * @param {Object} deps.drawerActions - Drawer state setters
 */
export function useRecipeActions({
  mealPlan,
  setMealPlan,
  productList,
  setProductList,
  leftovers,
  setLeftovers,
  setTotalCost,
  setAdjustedTotalCost,
  budget,
  numberOfPeople,
  userCountry,
  cookingStyle,
  dietaryPreferences,
  selectedCuisine,
  generateGroceryList,
  addReasoningStep,
  groceryOperationInProgress,
  setGroceryOperationInProgress,
  setImagesLoading,
  isPremium,
  onNavigateToWizard,
  drawerActions,
}) {
  const [isGeneratingAlternatives, setIsGeneratingAlternatives] = useState(false);
  const [isRemovingAll, setIsRemovingAll] = useState(false);
  const [isRemovingRecipe, setIsRemovingRecipe] = useState(false);

  const {
    setDrawerOpen,
    setSelectedRecipe,
    setDislikedRecipe,
    dislikedRecipe,
    setAlternativeRecipes,
    setAlternativesDrawerOpen,
    setRemoveAllDrawerOpen,
    setMealTypeToRemove,
    setRemoveRecipeDrawerOpen,
    setRecipeToRemove,
  } = drawerActions;

  // ─── Check empty & redirect ────────────────────────────────
  const checkAndRedirectIfEmpty = useCallback(() => {
    const hasValidRecipes =
      mealPlan &&
      mealPlan.length > 0 &&
      mealPlan.some((recipe) => !recipe.isPlaceholder);

    if (!hasValidRecipes) {
      console.log("✅ Meal plan is now empty after removal, redirecting to UserWizard");
      setTotalCost("");
      setAdjustedTotalCost(null);
      setTimeout(() => {
        if (onNavigateToWizard) onNavigateToWizard();
      }, 100);
    }
  }, [mealPlan, onNavigateToWizard, setTotalCost, setAdjustedTotalCost]);

  // ─── Recipe click ──────────────────────────────────────────
  const handleRecipeClick = useCallback(
    async (recipe) => {
      const isClickable =
        !recipe.isPlaceholder &&
        !recipe.fetchingDetailsForImage &&
        recipe.instructions;
      if (!isClickable) return;

      setSelectedRecipe(recipe);
      setDrawerOpen(true);

      // Analytics
      const mealTypeMap = { breakfast: MEAL_TYPE.BREAKFAST, lunch: MEAL_TYPE.LUNCH, dinner: MEAL_TYPE.DINNER };
      const mealType = recipe.mealType?.toLowerCase() || 'dinner';
      const recipeIndex = mealPlan.findIndex(r => r.title === recipe.title);
      trackRecipeViewed({
        meal_type: mealTypeMap[mealType] || MEAL_TYPE.DINNER,
        position_in_meal_type: (recipeIndex % 3) + 1,
        position_in_set: recipeIndex + 1,
        recipe_id: recipe.id || recipe.title,
      });
    },
    [mealPlan, setSelectedRecipe, setDrawerOpen]
  );

  // ─── Dislike recipe ────────────────────────────────────────
  const handleDislikeRecipe = useCallback(
    async (recipe) => {
      if (groceryOperationInProgress || recipe.isPlaceholder) return;

      const {
        getCachedAlternatives,
        cacheAlternatives,
        generateAlternativeRecipesAsync,
      } = await import("../AlternativeRecipes");
      const cachedAlternatives = getCachedAlternatives(recipe, mealPlan);

      if (cachedAlternatives && cachedAlternatives.length > 0) {
        setDislikedRecipe(recipe);
        setAlternativeRecipes(cachedAlternatives);
        setAlternativesDrawerOpen(true);
        setIsGeneratingAlternatives(false);
        addReasoningStep(`Showing saved replacement options for ${recipe.title}`);
        return;
      }

      setDislikedRecipe(recipe);
      setAlternativeRecipes([]);
      setAlternativesDrawerOpen(true);
      setIsGeneratingAlternatives(true);

      try {
        const recipeIdx = mealPlan.findIndex(r => r.title === recipe.title);
        const minStorageDays = 3 + (recipeIdx % 3);
        const alternatives = await generateAlternativeRecipesAsync(
          recipe, budget, numberOfPeople, dietaryPreferences, leftovers,
          addReasoningStep, userCountry, selectedCuisine, mealPlan, cookingStyle,
          minStorageDays
        );
        cacheAlternatives(recipe, alternatives, mealPlan);
        setAlternativeRecipes(alternatives);
      } catch (error) {
        console.error("Error generating alternatives:", error);
        if (error.name === "NoTriesRemainingError" || error.name === "InsufficientCreditsError") {
          // Keep drawer open with empty alternatives — user can still remove the meal for free.
          // RecipeAlternativesDrawer renders isLimitReached UI (only "Just remove it" option).
          setAlternativeRecipes([]);
          addReasoningStep(`No replacements left. You can still remove this meal.`);
        } else {
          addReasoningStep(`Could not find replacement meals: ${error.message}`);
          setAlternativeRecipes([]);
        }
      } finally {
        setIsGeneratingAlternatives(false);
      }
    },
    [
      groceryOperationInProgress, mealPlan, budget, numberOfPeople, dietaryPreferences,
      leftovers, userCountry, selectedCuisine, cookingStyle,
      addReasoningStep, setDislikedRecipe, setAlternativeRecipes,
      setAlternativesDrawerOpen,
    ]
  );

  // ─── Select alternative ────────────────────────────────────
  const handleSelectAlternative = useCallback(
    async (selectedAlternative, dislikedRecipe) => {
      if (!dislikedRecipe) return;

      // Note: replacement try is already consumed in AlternativeRecipes.js
      // (before the AI request), so we don't consume again here.

      const recipeToReplace = dislikedRecipe;

      // Close drawer immediately
      setAlternativesDrawerOpen(false);
      setDislikedRecipe(null);
      setAlternativeRecipes([]);

      // Guard: reject if selected alternative already exists in the plan
      const duplicate = mealPlan.find(
        r => r.title.toLowerCase() === selectedAlternative.title.toLowerCase()
          && r.title !== recipeToReplace.title
      );
      if (duplicate) {
        console.warn(`[handleSelectAlternative] Duplicate detected: "${selectedAlternative.title}" already in plan — skipping`);
        return;
      }

      // Analytics
      const mealTypeMap = { breakfast: MEAL_TYPE.BREAKFAST, lunch: MEAL_TYPE.LUNCH, dinner: MEAL_TYPE.DINNER };
      const mealType = recipeToReplace.mealType?.toLowerCase() || 'dinner';
      const recipeIndex = mealPlan.findIndex(r => r.title === recipeToReplace.title);
      trackRecipeReplaced({
        meal_type: mealTypeMap[mealType] || MEAL_TYPE.DINNER,
        position_in_meal_type: (recipeIndex % 3) + 1,
        position_in_set: recipeIndex + 1,
        old_recipe_id: recipeToReplace.id || recipeToReplace.title,
        new_recipe_id: selectedAlternative.id || selectedAlternative.title,
      });

      // Derive cooking complexity from user's cookingStyle setting
      const mealTypeKey = (selectedAlternative.mealType || recipeToReplace.mealType || '').toLowerCase();
      const complexity = cookingStyle?.[mealTypeKey]?.cookingTime || 'standard';
      const cookFor2Days = cookingStyle?.[mealTypeKey]?.cookFor2Days || false;

      // Slot-correct storage floor (position 0→3, 1→4, 2→5), same formula as
      // initial generation. Used both for custom-meal ingredient generation and
      // for the recipe-details fetch below.
      const minStorageDays = 3 + (recipeIndex % 3);

      // A favourite carries its own saved instructions + image. When both are
      // present we reuse them verbatim (no re-fetch) so the user's curated image
      // and title are preserved instead of being regenerated/renamed by the AI.
      const isFavourite = selectedAlternative.favoriteId != null;
      const hasSavedDetails = isFavourite
        && !!selectedAlternative.instructions
        && !!selectedAlternative.imageURL;

      const newRecipe = {
        ...selectedAlternative,
        title: selectedAlternative.title,
        description: selectedAlternative.description || "A delicious meal",
        mealType: selectedAlternative.mealType || recipeToReplace.mealType,
        cookingTime: selectedAlternative.cookingTime || "30 min",
        maxStorageTime: selectedAlternative.maxStorageTime || "3 days",
        ingredients: selectedAlternative.ingredients || [],
        isPlaceholder: false,
        // Favourites with saved details don't need a details/image fetch, so they
        // skip the placeholder spinner entirely.
        fetchingDetailsForImage: !hasSavedDetails,
        complexity,
        numberOfPeople,
        cookFor2Days,
      };

      // Replace recipe in UI (shows placeholder spinner unless it's a favourite)
      let updatedMealPlanForGrocery;
      const commitRecipe = (recipe) => {
        setMealPlan((prev) => {
          const updated = [...prev];
          const index = updated.findIndex(
            (r) => r.title === recipeToReplace.title || r.title === recipe.title
          );
          if (index !== -1) updated[index] = recipe;
          updatedMealPlanForGrocery = updated;
          return updated;
        });
      };
      commitRecipe(newRecipe);

      addReasoningStep(`Replacing ${recipeToReplace.title} with ${selectedAlternative.title}...`);

      setGroceryOperationInProgress(true);

      // Custom ("your own") meals — and any fallback alternative — arrive without
      // ingredients, so the grocery regen would add nothing for them. Expand the
      // title into real supermarket ingredients BEFORE grocery runs so the meal
      // contributes products, storage, and leftovers like a suggested alternative.
      const needsIngredients =
        selectedAlternative.isCustomMeal || !(selectedAlternative.ingredients?.length);
      if (needsIngredients) {
        try {
          const { generateCustomMealRecipeAsync } = await import("../AlternativeRecipes");
          const generated = await generateCustomMealRecipeAsync(newRecipe, {
            numberOfPeople,
            dietaryPreferences,
            userCountry,
            selectedCuisine,
            cookingStyle,
            budget,
            minStorageDays,
            addReasoningStep,
          });
          if (generated?.ingredients?.length) {
            newRecipe.ingredients = generated.ingredients;
            if (generated.cookingTime) newRecipe.cookingTime = generated.cookingTime;
            if (generated.maxStorageTime) newRecipe.maxStorageTime = generated.maxStorageTime;
            commitRecipe(newRecipe);
            console.log(`[handleSelectAlternative] 🧾 Custom meal expanded to ${generated.ingredients.length} ingredients`);
          } else {
            addReasoningStep(`Could not build a shopping list for "${selectedAlternative.title}".`);
          }
        } catch (error) {
          console.error("Error generating custom meal ingredients:", error);
          addReasoningStep(`Could not build a shopping list for "${selectedAlternative.title}".`);
        }
      }

      // Build storage preservation maps (after ingredients are finalized)
      const savedStorageMap = buildStorageMap(productList, updatedMealPlanForGrocery);
      console.log(`[handleSelectAlternative] 📦 Preserved storage data for ${savedStorageMap.size} products`);

      // Step 1: Generate grocery list FIRST
      let matchedProducts = null;
      try {
        if (updatedMealPlanForGrocery && updatedMealPlanForGrocery.length > 0) {
          const newGroceryResult = await generateGroceryList(updatedMealPlanForGrocery);
          if (newGroceryResult?.products) {
            matchedProducts = newGroceryResult.products;
            console.log(`[handleSelectAlternative] ✅ Grocery list ready with ${matchedProducts.length} categories`);
          }
        }
      } catch (error) {
        console.error("Error generating grocery list:", error);
        addReasoningStep(`Could not update your grocery list: ${error.message}`);
      } finally {
        setGroceryOperationInProgress(false);
      }

      // Step 2: Fetch recipe details WITH matched products.
      // Collect ingredient_storage entries into the storage map.
      const newRecipeStorageMap = new Map();
      const collectStorageEntries = (ingredientStorage) => {
        if (!Array.isArray(ingredientStorage)) return;
        for (const entry of ingredientStorage) {
          if (entry.name && entry.storage) {
            const days = Number(entry.shelf_life_days);
            newRecipeStorageMap.set(entry.name.toLowerCase().trim(), {
              storage: entry.storage,
              shelfLifeDays: days > 0 ? days : null,
            });
          }
        }
      };

      if (hasSavedDetails) {
        // Favourite with saved instructions + image: reuse them verbatim (no
        // re-fetch → preserves the curated image and avoids an AI rename). Storage
        // still comes from the favourite's own saved ingredient_storage.
        collectStorageEntries(selectedAlternative.instructions?.ingredient_storage);
        addReasoningStep(`Loaded ${selectedAlternative.title}`);
      } else {
        try {
          const recipeInstructions = await fetchRecipeDetailsAndGenerateImage(
            newRecipe, setMealPlan, setImagesLoading, addReasoningStep, userCountry, matchedProducts,
            minStorageDays
          );
          addReasoningStep(`Loaded ${selectedAlternative.title}`);
          collectStorageEntries(recipeInstructions?.ingredient_storage);
        } catch (error) {
          console.error("Error fetching recipe details:", error);
          addReasoningStep(`Could not load recipe details: ${error.message}`);
        }
      }

      // Step 3: Enrich products with storage data
      const enrichItem = createStorageEnricher(newRecipeStorageMap, savedStorageMap);

      setProductList((prevProducts) => {
        if (!prevProducts || prevProducts.length === 0) return prevProducts;
        return prevProducts.map((cat) => ({
          ...cat,
          items: cat.items.map(enrichItem),
        }));
      });

      setLeftovers((prevLeftovers) => {
        if (!prevLeftovers || prevLeftovers.length === 0) return prevLeftovers;
        return prevLeftovers.map(enrichItem);
      });
    },
    [
      mealPlan, productList, setMealPlan, setProductList, setLeftovers,
      generateGroceryList, addReasoningStep, userCountry, setImagesLoading,
      setGroceryOperationInProgress, setAlternativesDrawerOpen,
      setDislikedRecipe, setAlternativeRecipes, cookingStyle, numberOfPeople,
      budget, dietaryPreferences, selectedCuisine,
    ]
  );

  // ─── Remove recipe (from alternatives drawer) ─────────────
  const handleRemoveRecipe = useCallback(
    async (recipeToRemove) => {
      if (!recipeToRemove) return;

      setAlternativesDrawerOpen(false);
      setDislikedRecipe(null);
      setAlternativeRecipes([]);

      setMealPlan((prev) => prev.filter(r => r.title !== recipeToRemove.title));

      setGroceryOperationInProgress(true);
      try {
        await handleRemoveRecipeAsync(
          recipeToRemove, mealPlan, setMealPlan, generateGroceryList,
          addReasoningStep, () => {}, () => {}, () => {},
          productList, userCountry, numberOfPeople, budget
        );
        setTimeout(() => checkAndRedirectIfEmpty(), 500);
      } catch (error) {
        console.error("Error removing recipe:", error);
        addReasoningStep(`Could not remove this recipe: ${error.message}`);
      } finally {
        setGroceryOperationInProgress(false);
      }
    },
    [
      mealPlan, productList, setMealPlan, generateGroceryList, addReasoningStep,
      userCountry, numberOfPeople, budget, checkAndRedirectIfEmpty,
      setGroceryOperationInProgress, setAlternativesDrawerOpen,
      setDislikedRecipe, setAlternativeRecipes,
    ]
  );

  // ─── Confirm remove recipe (from confirmation drawer) ─────
  const handleConfirmRemoveRecipe = useCallback(
    async (recipeToRemove) => {
      if (!recipeToRemove || groceryOperationInProgress) return;

      setIsRemovingRecipe(true);
      await new Promise((resolve) => setTimeout(resolve, 150));

      setRemoveRecipeDrawerOpen(false);
      setDrawerOpen(false);
      setIsRemovingRecipe(false);
      setGroceryOperationInProgress(true);

      try {
        await handleRemoveRecipeAsync(
          recipeToRemove, mealPlan, setMealPlan, generateGroceryList,
          addReasoningStep, setAlternativesDrawerOpen, setIsGeneratingAlternatives,
          setDislikedRecipe, productList, userCountry, numberOfPeople, budget
        );
      } catch (error) {
        console.error("Error removing recipe:", error);
        addReasoningStep(`Could not remove this recipe: ${error.message}`);
      } finally {
        setGroceryOperationInProgress(false);
      }

      setRecipeToRemove(null);
      setTimeout(() => checkAndRedirectIfEmpty(), 500);
    },
    [
      groceryOperationInProgress, mealPlan, productList, setMealPlan,
      generateGroceryList, addReasoningStep, userCountry, numberOfPeople, budget,
      checkAndRedirectIfEmpty, setGroceryOperationInProgress,
      setRemoveRecipeDrawerOpen, setDrawerOpen, setRecipeToRemove,
      setAlternativesDrawerOpen, setDislikedRecipe,
    ]
  );

  // ─── Remove all meals of a type ───────────────────────────
  const handleConfirmRemoveAll = useCallback(
    async (mealTypeToRemove) => {
      if (!mealTypeToRemove || groceryOperationInProgress) return;

      setIsRemovingAll(true);
      const mealTypeBeingRemoved = mealTypeToRemove;

      await new Promise((resolve) => setTimeout(resolve, 150));

      setRemoveAllDrawerOpen(false);
      setMealTypeToRemove(null);
      setIsRemovingAll(false);
      setGroceryOperationInProgress(true);

      try {
        addReasoningStep(`Removing all ${mealTypeBeingRemoved.toLowerCase()} meals...`);

        const recipesToRemove = mealPlan.filter(r => r.mealType === mealTypeBeingRemoved);
        const filteredMealPlan = mealPlan.filter(r => r.mealType !== mealTypeBeingRemoved);
        setMealPlan(filteredMealPlan);

        if (filteredMealPlan.length > 0) {
          if (productList && productList.length > 0 && userCountry && numberOfPeople) {
            addReasoningStep(`Updating your grocery list...`);
            try {
              const smartResult = await smartRecalculateBulkRemoval(
                recipesToRemove, filteredMealPlan, productList, {
                  country: userCountry, numberOfPeople, budget,
                  onProgress: (progress) => {
                    addReasoningStep(`Grocery list: ${progress.message} (${progress.progress || 0}%)`);
                  },
                }
              );
              if (smartResult && smartResult.products) {
                addReasoningStep(`Grocery list updated.`);
              } else {
                addReasoningStep(`Updating your grocery list another way...`);
              }
            } catch (error) {
              console.error("Smart bulk recalculation error:", error);
              addReasoningStep(`Updating your grocery list another way...`);
            }
            // Always regenerate via standard path for now
            await generateGroceryList(filteredMealPlan);
          } else {
            addReasoningStep(`Updating your grocery list for the remaining ${filteredMealPlan.length} recipes...`);
            await generateGroceryList(filteredMealPlan);
          }
        } else {
          addReasoningStep(`No recipes remain, so the grocery list is being cleared...`);
          setProductList([]);
          setTotalCost("€0.00");
          setAdjustedTotalCost(null);
          setLeftovers([]);
        }

        addReasoningStep(`Removed all ${mealTypeBeingRemoved.toLowerCase()} meals and updated your grocery list.`);
      } catch (error) {
        console.error("Error removing all meals:", error);
        addReasoningStep(`Could not remove these meals: ${error.message}`);
      } finally {
        setGroceryOperationInProgress(false);
        setIsRemovingAll(false);
        setRemoveAllDrawerOpen(false);
        setMealTypeToRemove(null);
      }

      setTimeout(() => checkAndRedirectIfEmpty(), 500);
    },
    [
      groceryOperationInProgress, mealPlan, productList, setMealPlan,
      setProductList, setTotalCost, setAdjustedTotalCost, setLeftovers, generateGroceryList,
      addReasoningStep, userCountry, numberOfPeople, budget,
      checkAndRedirectIfEmpty, setGroceryOperationInProgress,
      setRemoveAllDrawerOpen, setMealTypeToRemove,
    ]
  );

  // ─── Remove all click (opens drawer) ──────────────────────
  const handleRemoveAllClick = useCallback(
    (mealType) => {
      setMealTypeToRemove(mealType);
      setRemoveAllDrawerOpen(true);
    },
    [setMealTypeToRemove, setRemoveAllDrawerOpen]
  );

  // ─── Close handlers ───────────────────────────────────────
  const handleCloseRemoveAllDrawer = useCallback(() => {
    setRemoveAllDrawerOpen(false);
    setMealTypeToRemove(null);
  }, [setRemoveAllDrawerOpen, setMealTypeToRemove]);

  const handleCloseRemoveRecipeDrawer = useCallback(() => {
    setRemoveRecipeDrawerOpen(false);
    setRecipeToRemove(null);
  }, [setRemoveRecipeDrawerOpen, setRecipeToRemove]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedRecipe(null), 300);
  }, [setDrawerOpen, setSelectedRecipe]);

  const handleGenerateMoreAlternatives = useCallback(async () => {
    if (isGeneratingAlternatives || !dislikedRecipe) return;

    const { generateAlternativeRecipesAsync } = await import("../AlternativeRecipes");

    setIsGeneratingAlternatives(true);
    try {
      const recipeIdx = mealPlan.findIndex(r => r.title === dislikedRecipe.title);
      const minStorageDays = 3 + (recipeIdx % 3);
      const moreAlternatives = await generateAlternativeRecipesAsync(
        dislikedRecipe, budget, numberOfPeople, dietaryPreferences, leftovers,
        addReasoningStep, userCountry, selectedCuisine, mealPlan, cookingStyle,
        minStorageDays
      );
      setAlternativeRecipes(prev => [...prev, ...moreAlternatives]);
    } catch (error) {
      console.error("Error generating more alternatives:", error);
    } finally {
      setIsGeneratingAlternatives(false);
    }
  }, [
    isGeneratingAlternatives, dislikedRecipe, mealPlan, budget, numberOfPeople,
    dietaryPreferences, leftovers, userCountry, selectedCuisine, cookingStyle,
    addReasoningStep, setAlternativeRecipes,
  ]);

  return {
    isGeneratingAlternatives,
    isRemovingAll,
    isRemovingRecipe,
    handleRecipeClick,
    handleDislikeRecipe,
    handleGenerateMoreAlternatives,
    handleSelectAlternative,
    handleRemoveRecipe,
    handleConfirmRemoveRecipe,
    handleConfirmRemoveAll,
    handleRemoveAllClick,
    handleCloseRemoveAllDrawer,
    handleCloseRemoveRecipeDrawer,
    handleCloseDrawer,
  };
}
